import { useEffect, useRef, useState } from 'react';

import { useProjectStore } from '@/lib/zustand/useStore';
import { useParams } from "react-router-dom";

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCheck, SlidersHorizontal } from 'lucide-react';

import Combobox from '@/components/custom/Combobox';
import { QuestionForm } from '@/features/question/components/organisms/QuestionForm/QuestionForm';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { AddQuestionModalProps } from '.';
import { questionFormDefaultValues, QuestionFormRef, QuestionFormSchemaType } from '../QuestionForm';
import { useAccessImperativeHandle } from '@/hooks/useAccessImperativeHandle';
import { useGetOrganisationQuestions } from '@/features/question_bank/hooks/useQuery/useGetOrganisationQuestions';

export function AddQuestionModal<Q extends Tables<'question'>>({
    show,
    onHide,
    onSubmit,
    defaultStep = 1,
    crm,
    crmProject,
    crmConfig,
    questions,
}: Readonly<AddQuestionModalProps<Q>>) {
    // TODO check why component refreshes all the time
    // TODO Move orgId to props
    const { orgId } = useParams();
    const { project } = useProjectStore();
    const questionFormRef = useRef<QuestionFormRef>(null);
    const resolvedQuestionFormRef = useAccessImperativeHandle(questionFormRef)

    const [step, setStep] = useState(defaultStep);
    const [selectedDefaultValues, setSelectedDefaultValues] = useState<QuestionFormSchemaType>(questionFormDefaultValues);
    const [questionTemplateBoxValue, setQuestionTemplateBoxValue] = useState<string | null>(null);
    const [isFormDisabled, setIsFormDisabled] = useState(false);

    const { data: templateQuestionsData, isLoading: questionsLoading } = useGetOrganisationQuestions({
        crm_id: crm?.id ?? (project?.crm_id || undefined),
        organisation_id: orgId ? Number(orgId) : undefined
    });

    const onCloseQuestionModal = () => {
        setStep(defaultStep);
        onHide();
        setQuestionTemplateBoxValue(null);
    }

    const onSubmitQuestionForm = (values: QuestionFormSchemaType) => {
        // console.log(resolvedQuestionFormRef?.form?.getValues())
        // console.log(values)
        onSubmit(values);
        onCloseQuestionModal();
        setStep(defaultStep);
        resolvedQuestionFormRef?.resetForm && resolvedQuestionFormRef?.resetForm();
    }

    const goBackHandler = () => {
        setStep(1);
        resolvedQuestionFormRef?.resetForm && resolvedQuestionFormRef?.resetForm();
    }
    useEffect(()=>{
        resolvedQuestionFormRef?.resetForm && resolvedQuestionFormRef?.resetForm();
    },[resolvedQuestionFormRef, show])
    useEffect(() => {
        if (questionTemplateBoxValue) {
            setIsFormDisabled(true);
            const question = templateQuestionsData?.find(question => question.id === questionTemplateBoxValue);

            if (question) {
                setSelectedDefaultValues(question);
            }
        }
    }, [crm, questionTemplateBoxValue, templateQuestionsData])
    console.log(crm)
    console.log(crmProject)
    console.log(crmConfig)
    return (
        <Dialog open={show} onOpenChange={onCloseQuestionModal} defaultOpen={false} >
            <DialogContent className="max-w-6xl">
                <DialogHeader>
                    <DialogTitle className='ps-3'>
                        <div className="p-0 m-0 text-start">
                            {(step === 1) && "Add new question"}
                            {(step === 2) && "Select existing question"}
                            {(step === 3) && "Fill question details"}
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] px-3">
                    <hr className="mb-3" />

                    {(step === 1) && (
                        <div className="flex flex-col items-center px-1">
                            <Button variant={'outline'} className="w-full max-w-md mb-2" onClick={() => setStep(3)}>
                                <SlidersHorizontal className="me-2" size={16} />
                                Add custom question
                            </Button>
                            <Button variant={'outline'} className="w-full max-w-md " onClick={() => setStep(2)}>
                                <CheckCheck className="me-2" size={16} />
                                Add existing question
                            </Button>
                        </div>
                    )}

                    {(step === 2) && (
                        <div className={`px-1`}>
                            {templateQuestionsData && templateQuestionsData.length > 0 && (
                                <Combobox
                                    mode="single"
                                    inDialog
                                    items={[{ label: "Clear selection", value: "" }, ...templateQuestionsData.map((question, i) => ({ label: `${i + 1}. ${question.question}`, value: question.id }))]}
                                    setSelectedValue={(value: string | string[] | null) => {
                                        if (!Array.isArray(value)) {
                                            setQuestionTemplateBoxValue(value)
                                        }
                                    }}
                                    selectedValue={questionTemplateBoxValue}
                                    triggerLabel="Select question template (optional)"
                                />
                            )}
                            {(!templateQuestionsData || templateQuestionsData?.length === 0) && (
                                <p className='py-4 text-center'>No Question Templates found</p>
                            )}
                            {(!templateQuestionsData && questionsLoading) && ("Couldn't load questions data")}
                        </div>
                    )}


                    <div className={`px-1 ${step === 3 ? 'block' : 'hidden'}`}>
                        <QuestionForm
                            defaultValues={selectedDefaultValues}
                            ref={questionFormRef}
                            crm={crm}
                            crmProject={crmProject}
                            crmConfig={crmConfig}
                            isFormDisabled={isFormDisabled}
                            questions={questions}
                            crmSystem={project?.crm_system}
                        />
                    </div>

                </ScrollArea>
                <DialogFooter className="px-3">
                    {(step === 1) && (
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" type="button" onClick={onCloseQuestionModal}>Close</Button>
                        </div>
                    )}
                    {(step === 2) && (
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" type="button" onClick={goBackHandler}>Back</Button>
                            <Button size="sm" type="button" onClick={() => setStep(3)}>Next</Button>
                        </div>
                    )}
                    {(step === 3) && (
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            {defaultStep !== 3 && (
                                <Button size="sm" variant="secondary" type="button" onClick={goBackHandler}>Back</Button>
                            )}
                            <Button size="sm" type="button" onClick={()=>{
                                resolvedQuestionFormRef?.form?.handleSubmit(onSubmitQuestionForm, err => console.log(err))();
                            }}>Submit</Button>
                        </div>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
